/*
 * Filtering the collection without reloading the page.
 *
 * The chips are, and stay, ordinary links. That is deliberate: with this file
 * missing or JavaScript switched off every chip still filters, by a normal page
 * load, which is how the section was accepted. Everything below is an upgrade
 * layered over working markup, never a replacement for it.
 *
 * The section is re-rendered through the theme's own Section Rendering API,
 * `sectionRenderer.renderSection`, which is what the stock facets component
 * uses. That means one fetch, one morph, and no second mechanism of ours to
 * keep in step with the theme.
 *
 * Both the chips above the grid and the ones inside the filter drawer are
 * intercepted; the drawer keeps its open state across the swap.
 *
 * The swap also has to hold the page still, and that is less obvious than it
 * sounds: past 990 this theme does not scroll the window at all, it scrolls
 * .page-wrapper, which the browser neither restores on history navigation nor
 * leaves alone across a morph. So the position is read before the swap and put
 * back after it.
 */

import { sectionRenderer } from '@theme/section-renderer';

const SECTION = '.pp-collection';
const LINK = 'a.pp-collection__chip, a.pp-collection__reset';

/**
 * True for a click the browser would treat as a plain navigation. A modified
 * click means the visitor asked for a new tab or a download, so it is left to
 * the browser untouched.
 *
 * @param {MouseEvent} event
 */
function isPlainClick(event) {
  return event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey;
}

/**
 * The element that actually scrolls. Past the theme's 990 breakpoint html and
 * body are locked at viewport height and .page-wrapper carries the overflow;
 * below it the document scrolls as usual.
 *
 * @returns {Element}
 */
function scrollingHost() {
  const wrapper = document.querySelector('.page-wrapper');

  if (wrapper instanceof HTMLElement && wrapper.scrollHeight > wrapper.clientHeight) return wrapper;

  return document.scrollingElement ?? document.documentElement;
}

/**
 * Puts the scroll position back without animating it. The wrapper is set to
 * smooth scrolling, and a smooth jump back to where you already were reads as a
 * glitch rather than as staying put.
 *
 * @param {Element} host
 * @param {number} top
 */
function restoreScroll(host, top) {
  if (host.scrollTop === top) return;

  const style = /** @type {HTMLElement} */ (host).style;
  const previous = style.scrollBehavior;

  style.scrollBehavior = 'auto';
  host.scrollTop = top;
  style.scrollBehavior = previous;
}

/**
 * @param {Element} element
 * @returns {string | null}
 */
function sectionIdFor(element) {
  const root = element.closest(SECTION);
  return root instanceof HTMLElement ? root.dataset.sectionId ?? null : null;
}

/**
 * Re-renders the section at the given address, and falls back to a normal page
 * load if anything goes wrong. A filter that silently does nothing would be
 * worse than the reload this file exists to avoid.
 *
 * @param {string} sectionId
 * @param {URL} url
 */
async function render(sectionId, url) {
  const host = scrollingHost();
  const top = host.scrollTop;

  try {
    await sectionRenderer.renderSection(sectionId, { url });

    /* After the morph, and again on the next frame, because the section's own
       height settles a frame later and a taller or shorter grid moves the
       scroller under us. */
    restoreScroll(host, top);
    requestAnimationFrame(() => restoreScroll(host, top));
  } catch {
    window.location.assign(url.toString());
  }
}

document.addEventListener('click', (event) => {
  if (!isPlainClick(event)) return;

  const target = event.target;
  if (!(target instanceof Element)) return;

  const link = target.closest(LINK);
  if (!(link instanceof HTMLAnchorElement)) return;

  /*
   * The drawer is included, and its open panel survives.
   *
   * Its <details> carries data-skip-node-update, which morph reads as "leave
   * this node's own attributes alone but still reconcile its children". So the
   * open attribute is kept while the chips inside are refreshed, which is the
   * combination this needs: the panel stays where the visitor left it and no
   * chip is left holding an address from before the last choice.
   */

  const sectionId = sectionIdFor(link);
  if (!sectionId) return;

  event.preventDefault();

  const url = new URL(link.href, window.location.href);
  history.pushState({ ppCollectionSection: sectionId }, '', url.toString());
  render(sectionId, url);
});

/* Back and forward move through the filter states rather than out of the page. */
window.addEventListener('popstate', () => {
  const root = document.querySelector(SECTION);
  if (!(root instanceof HTMLElement)) return;

  const sectionId = root.dataset.sectionId;
  if (!sectionId) return;

  render(sectionId, new URL(window.location.href));
});
